import React, {useMemo} from 'react';
import SentryEchart from "../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../hooks/use-graph-viewport-width.js";
import {fluidPx} from "../../../../../utils/responsive-graph.js";

const ACTION_LABELS = {
    ATTENDANCE: 'Atendimento',
    COMMENT: 'Comentário',
    REFERRED: 'Encaminhamento',
    INTERVIEW: 'Entrevista',
    CALL: 'Ligação',
    PATROL: 'Ronda',
    VISIT: 'Visita',
    GUIDANCE: 'Orientação'
};

const ACTION_COLORS = {
    ATTENDANCE: '#20609D',
    COMMENT: '#3B82C7',
    REFERRED: '#50A6F9',
    INTERVIEW: '#87C8FF',
    CALL: '#64C5C5',
    PATROL: '#79EBEB',
    VISIT: '#E9D89D',
    GUIDANCE: '#f6b593'
};

const SosHomeActivitiesGraph = ({data}) => {
    const vw = useGraphViewportWidth();

    const option = useMemo(() => {
        if (!data?.activitiesByAction) return {};
        const labelFs = fluidPx(11, 18, vw);

        const dates = Object.keys(data.activitiesByAction);
        const actions = Object.keys(ACTION_LABELS);

        const series = actions.map(action => ({
            name: ACTION_LABELS[action],
            type: 'bar',
            stack: 'activities',
            barWidth: '50%',
            data: dates.map(date => data.activitiesByAction[date]?.[action] ?? 0),
            itemStyle: {
                color: ACTION_COLORS[action],
                borderRadius: 8,
                borderColor: 'transparent',
                borderWidth: 2
            }
        }));

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {type: 'shadow'},
                extraCssText: 'max-width: 320px; white-space: normal;',
                formatter: (params) => {
                    const [y, m, d] = params[0].axisValue.split('-');
                    const header = `${d}/${m}/${y}`;
                    const lines = params
                        .filter(p => p.value > 0)
                        .map(p => `${p.marker} ${p.seriesName}: <b>${p.value}</b>`)
                        .join('<br/>');
                    return `${header}<br/>${lines}`;
                }
            },
            legend: {
                type: 'scroll',
                animation: false,
                bottom: 0,
                left: 'center',
                icon: 'circle',
                itemGap: 8,
                itemWidth: 8,
                itemHeight: 8,
                textStyle: {fontSize: labelFs, color: '#676a6c'}
            },
            grid: {top: 12, left: 4, right: 8, bottom: 48, containLabel: true},
            xAxis: {
                type: 'category',
                data: dates,
                axisLabel: {
                    interval: 0,
                    lineHeight: labelFs * 1.4,
                    rich: {
                        weekday: {fontSize: labelFs, color: '#676a6c'},
                        date: {fontSize: labelFs, color: '#676a6c'}
                    },
                    formatter: (value) => {
                        const [y, m, d] = value.split('-');
                        const weekday = new Date(+y, +m - 1, +d)
                            .toLocaleDateString('pt-BR', {weekday: 'short'})
                            .replace('.', '')
                            .replace(/^./, c => c.toUpperCase());
                        return `{weekday|${weekday}}\n{date|${d}/${m}}`;
                    }
                },
                axisLine: {
                    lineStyle: { color: "#BEBEBE" }
                },
            },
            yAxis: {
                type: 'value',
                axisLabel: {show: false},
                axisLine: {
                    show: true,
                    lineStyle: { color: "#BEBEBE" }
                },
                axisTick: {show: false},
                splitLine: {show: false}
            },
            series
        };
    }, [data?.activitiesByAction, vw]);

    return (
        <SentryEchart
            option={option}
            title={"Atividades dos últimos 7 dias"}
            titleClass={"text-[#676a6c]!"}
        />
    );
};

export default SosHomeActivitiesGraph;